import { Clock, RefreshCw, LogOut, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface LoadingRecoveryOverlayProps {
  onRetry: () => void;
  onSignOut: () => void;
  onDeleteAccount?: () => void;
  title?: string;
  description?: string;
}

/**
 * Recovery UI component shown when profile loading times out
 * Follows 2025 best practices: never auto-logout on timeout,
 * give user control with Retry, Sign Out, and Delete options
 */
export const LoadingRecoveryOverlay = ({ 
  onRetry, 
  onSignOut,
  onDeleteAccount,
  title = "Connection Slow",
  description = "We're having trouble loading your account. This might be due to a slow connection."
}: LoadingRecoveryOverlayProps) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="max-w-md w-full p-8 text-center space-y-6 border-amber-500/20">
        <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto animate-pulse">
          <Clock className="w-8 h-8 text-amber-500" />
        </div>
        <div>
          <h2 className="text-2xl font-bold mb-2">{title}</h2>
          <p className="text-muted-foreground">{description}</p>
        </div>
        <div className="space-y-3">
          <Button onClick={onRetry} className="w-full gap-2">
            <RefreshCw className="w-4 h-4" />
            Try Again
          </Button>
          <Button onClick={onSignOut} variant="outline" className="w-full gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
          {onDeleteAccount && (
            <Button onClick={onDeleteAccount} variant="destructive" className="w-full gap-2">
              <Trash2 className="w-4 h-4" />
              Delete This Account
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
